import { faChevronDown, faChevronUp, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ButtonAtom } from "../../../../../components/atoms/atom";
import {
    applyAufmassResultRaw,
    markDirtyAfterMutation,
    selectAddedGroupsV2,
    selectFormDataV2,
    selectPositionUnitMapV2,
    selectReadonlyV2,
    setAddGroupModal,
    setAufmassError,
    setDeleteGroupModal,
    setDeleteLineModal,
} from "../../../../../store/slices/aufmassV2Slice";
import { selectCurrentProjectId } from "../../../../../store/slices/projectDetailsSlice";
import { useDeleteAufmassGroupMutation } from "../../../../../store/services/aufmass";
import { formatQuantityToLocale, showToast } from "../../../../../utils/method";
import DeleteConfirmationModal from "../../components/sections/DeleteConfirmationModal";
import AddGroupModalV2 from "../AddGroupModalV2";
import AddLineModalV2 from "../AddLineModalV2";
import DeleteLineModalV2 from "../DeleteLineModalV2";
import GroupItemV2 from "./GroupItemV2";
import LineItemV2 from "./LineItemV2";
import './NewMeasurementSectionV2.scss';

function NewMeasurementSectionV2({ position }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [isExpanded, setIsExpanded] = useState(true); // Start expanded by default
    const readonly = useSelector(selectReadonlyV2);
    const positionId = position.id; // LV position ID

    // Use V2 selectors
    const addedGroups = useSelector(selectAddedGroupsV2);
    const positionUnitMap = useSelector(selectPositionUnitMapV2);
    const projectId = useSelector(selectCurrentProjectId);
    const formData = useSelector(selectFormDataV2);
    const [deleteAufmassGroup] = useDeleteAufmassGroupMutation();

    // Get additional state for delete functionality
    const { showDeleteGroupModal, deleteGroupData } = useSelector((state) => state.aufmassV2);

    // After backend changes, addedGroups now uses hierarchical position ID as keys (consistent with positions)
    const groups = useMemo(
        () => addedGroups[positionId] || [],
        [addedGroups, positionId],
    );

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Calculate total quantity
    const totalQuantity = useMemo(() => {
        return groups.reduce((sum, group) => {
            return (
                sum +
                (group.lines || []).reduce((lineSum, line) => {
                    return lineSum + parseFloat(line.quantity || 0);
                }, 0)
            );
        }, 0);
    }, [groups]);

    const handleAddGroup = useCallback(
        (position) => {
            dispatch(setAddGroupModal({ show: true, position }));
        },
        [dispatch],
    );

    const handleEditGroup = useCallback(
        (position, group) => {
            // Implement edit functionality by opening the modal with edit data
            const editData = {
                groupId: group.groupId,
                groupName: group.groupName,
                lines: group.lines || [],
                positionId: positionId, // Use hierarchical position ID
                isEditMode: true,
            };

            dispatch(setAddGroupModal({
                show: true,
                position: position,
                editData: editData
            }));
        },
        [dispatch, positionId],
    );

    const handleDeleteGroup = useCallback(
        (position, groupId) => {
            // Get the group to get its name for the confirmation modal
            const group = groups.find(g => g.groupId === groupId);

            // Use group name if available, otherwise use a generic fallback
            const groupDisplayName = group?.groupName?.trim() || null;

            dispatch(setDeleteGroupModal({
                show: true,
                groupId: groupId,
                positionId: positionId, // Use hierarchical position ID now
                groupName: groupDisplayName,
            }));
        },
        [dispatch, positionId, groups],
    );

    const handleEditLine = useCallback(
        (position, line, groupId) => {
            // For V2, we might need to implement line editing differently
            showToast(t("aufmass.edit-line-not-implemented"), { type: "info" });
        },
        [t],
    );

    const handleDeleteLine = useCallback(
        (position, lineId, groupId, groupIndex, lineIndex) => {
            // Find the line data to include in the modal
            const group = groups.find(g => g.groupId === groupId);
            const line = group?.lines?.find(l => l.id === lineId);

            dispatch(setDeleteLineModal({
                show: true,
                line: line,
                groupIndex: groupIndex,
                lineIndex: lineIndex
            }));
        },
        [dispatch, groups],
    );

    const handleConfirmDeleteGroup = useCallback(async () => {
        if (!deleteGroupData) {
            return;
        }

        const { groupId } = deleteGroupData;

        try {
            const response = await deleteAufmassGroup({
                projectId,
                aufmassId: formData.id,
                groupId,
            }).unwrap();
            dispatch(applyAufmassResultRaw(response));
            dispatch(markDirtyAfterMutation());

            showToast(t("aufmass.group-deleted-successfully"), {
                type: "success",
            });

            dispatch(setDeleteGroupModal({ show: false }));
        } catch (error) {
            dispatch(setAufmassError(error?.data?.message || error?.message));
            showToast(error?.data?.message || error?.message || t("aufmass.group-delete-failed"), {
                type: "error",
            });
        }
    }, [dispatch, deleteGroupData, projectId, formData.id, deleteAufmassGroup, t]);

    return (
        <div className="new-measurement-section__wrapper">
            {/* Collapsible Header */}
            <div
                className={`new-measurement-section__header${isExpanded ? " new-measurement-section__header--expanded" : ""}`}
                onClick={toggleExpanded}
            >
                <div className="new-measurement-section__header-left">
                    <FontAwesomeIcon
                        icon={isExpanded ? faChevronUp : faChevronDown}
                        className="new-measurement-section__header-icon"
                    />
                    <span className="new-measurement-section__header-title">
                        {t("aufmass.new-measurements")}
                    </span>
                    <span className="new-measurement-section__count-badge">
                        {groups.length}{" "}
                        {groups.length === 1
                            ? t("aufmass.group")
                            : t("aufmass.group-plural")}
                    </span>
                </div>
                <div className="new-measurement-section__header-right">
                    <div className="new-measurement-section__total-text">
                        {t("aufmass.total")}:{" "}
                        {formatQuantityToLocale(totalQuantity)}{" "}
                        {positionUnitMap[positionId] || position.unit}
                    </div>
                    {!readonly && (
                        <ButtonAtom
                            className="btn btn-primary new-measurement-section__add-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAddGroup(position);
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} className="new-measurement-section__add-icon" />
                            {t("aufmass.add")}
                        </ButtonAtom>
                    )}
                </div>
            </div>

            {/* Collapsible Content */}
            {isExpanded && (
                <div className="new-measurement-section__content">
                    {groups.length === 0 ? (
                        <div className="text-muted fst-italic text-center p-5 new-measurement-section__empty-state">
                            {readonly
                                ? t("aufmass.no-measurements-readonly")
                                : t("aufmass.no-measurements-click-add")
                            }
                        </div>
                    ) : (
                        <div>
                            {groups.map((group, groupIndex) => (
                                <div key={group.groupId || groupIndex} className="new-measurement-section__group-item">
                                    <GroupItemV2.Header
                                        group={group}
                                        groupIndex={groupIndex}
                                        position={position}
                                        handleEditGroup={() => handleEditGroup(position, group)}
                                        handleDeleteGroup={() => handleDeleteGroup(position, group.groupId)}
                                        readonly={readonly}
                                    />
                                    {group.lines && group.lines.map((line, lineIndex) => (
                                        <LineItemV2
                                            group={group}
                                            key={line.id || lineIndex}
                                            line={line}
                                            lineIndex={lineIndex}
                                            groupIndex={groupIndex}
                                            position={position}
                                            onEdit={() => handleEditLine(position, line, group.groupId)}
                                            onDelete={() => handleDeleteLine(position, line.id, group.groupId, groupIndex, lineIndex)}
                                            readonly={readonly}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Add the V2 Modals */}
            <AddGroupModalV2 />
            <AddLineModalV2 />
            <DeleteLineModalV2 />

            {/* Delete confirmation modal */}
            {showDeleteGroupModal && (
                <DeleteConfirmationModal
                    show={showDeleteGroupModal}
                    onClose={() => dispatch(setDeleteGroupModal({ show: false }))}
                    onConfirm={handleConfirmDeleteGroup}
                    title={
                        deleteGroupData?.groupName
                            ? t("aufmass.delete-group", { name: deleteGroupData.groupName })
                            : t("aufmass.delete-group-unnamed")
                    }
                    message={t("aufmass.delete-group-warning")}
                />
            )}
        </div>
    );
}

export default NewMeasurementSectionV2;
